import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { Activity, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { Alert } from "./ui-bits";

// Segundos de la apertura que decide el vídeo (ver "EL HOOK" en master_prompt.py).
const HOOK_SEC = 30;
// relativeRetentionPerformance: 0,5 es la mediana de YouTube para vídeos de duración parecida.
// Por debajo, la apertura rinde peor que la mitad de los vídeos comparables.
const MEDIANA_YT = 0.5;

const pct = (v) => (v == null ? "—" : `${v}%`);
const mmss = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;

export default function RetentionPanel({ projectId }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null); // null = cargando

  useEffect(() => {
    let vivo = true;
    api.get(`/projects/${projectId}/youtube/retention`)
      .then(({ data: d }) => { if (vivo) setData(d); })
      .catch(() => { if (vivo) setData({ disponible: false, motivo: "error_analytics" }); });
    return () => { vivo = false; };
  }, [projectId]);

  if (data === null) {
    return (
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-500" data-testid="retention-loading">
        <Loader2 className="animate-spin" size={16} /> {t("retention.loading")}
      </div>
    );
  }

  if (!data.disponible) {
    // "sin_datos_aun" es el caso normal recién subido, no un fallo: YouTube no publica la curva
    // hasta que hay visionados suficientes. Se avisa en tono informativo, no de error.
    const esError = data.motivo === "error_analytics";
    return (
      <Alert type={esError ? "warning" : "info"} testid={`retention-${data.motivo}`}>
        {t(`retention.motivo.${data.motivo}`, t("retention.motivo.sin_datos_aun"))}
      </Alert>
    );
  }

  const { hook = {}, duracion_sec: dur, vs_similares_hook: vs, caida_hook_pct: caida, hook_tipo: molde } = data;
  const porDebajo = vs != null && vs < MEDIANA_YT;
  const curva = (data.puntos || []).map((p) => ({
    seg: Math.round(p.ratio * dur),
    retencion: Math.round(p.retencion * 1000) / 10,
  }));

  return (
    <div className="space-y-3" data-testid="retention-panel">
      <div className="flex items-center gap-2">
        <Activity size={18} className="text-slate-700" />
        <h4 className="text-base font-bold font-head text-slate-900">{t("retention.title")}</h4>
        {molde && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
            {t(`retention.molde.${molde}`, molde)}
          </span>
        )}
      </div>

      {/* Los tres hitos del hook. La jerarquía es deliberada: el segundo 30 es el que decide,
          va más grande y con color propio; 5s y 15s son contexto de cómo se llegó hasta ahí. */}
      <div className="flex items-end gap-4">
        <Hito label={t("retention.at", { s: 5 })} value={pct(hook.seg_5)} />
        <Hito label={t("retention.at", { s: 15 })} value={pct(hook.seg_15)} />
        <Hito label={t("retention.at", { s: 30 })} value={pct(hook.seg_30)} destacado />
        {caida != null && (
          <div className="ml-auto text-right">
            <div className="text-sm font-bold text-slate-900">−{caida}%</div>
            <div className="text-xs text-slate-500">{t("retention.dropLabel")}</div>
          </div>
        )}
      </div>

      {/* El número honesto: no cuánta gente se va (se va en todos los vídeos), sino si se va
          MÁS que en vídeos comparables. Es lo único que dice si hay un problema propio.
          El TEXTO es neutro a propósito (ago-2026, feedback directo): el vídeo lo genera la
          propia IA de YTMethod, así que un mensaje tipo "rindes por debajo de la mediana...
          es donde más tienes que ganar" suena a la herramienta criticando su propio trabajo.
          El color/icono (ámbar+flecha abajo vs esmeralda+flecha arriba) ya transmite la
          comparación sin necesidad de decirlo con ese tono. */}
      {vs != null && (
        <div
          data-testid="retention-vs"
          className={`flex items-start gap-2 rounded-xl border-2 px-3 py-2 text-sm font-semibold ${
            porDebajo ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          {porDebajo ? <TrendingDown size={18} className="shrink-0 mt-0.5" /> : <TrendingUp size={18} className="shrink-0 mt-0.5" />}
          <span>{t("retention.vsSimilares", { pctil: Math.round((vs || 0) * 100) })}</span>
        </div>
      )}

      {curva.length > 0 && (
        <div className="h-40 w-full" data-testid="retention-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={curva} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
              <defs>
                <linearGradient id="gradRet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#dc2626" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#dc2626" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis dataKey="seg" tickFormatter={mmss} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94a3b8" width={44} unit="%" />
              <Tooltip
                labelFormatter={(s) => mmss(s)}
                formatter={(v) => [`${v}%`, t("retention.watching")]}
                contentStyle={{ borderRadius: 12, border: "2px solid #e2e8f0", fontSize: 13 }}
              />
              {dur > HOOK_SEC && (
                <ReferenceLine
                  x={curva.reduce((a, p) => (Math.abs(p.seg - HOOK_SEC) < Math.abs(a - HOOK_SEC) ? p.seg : a), curva[0].seg)}
                  stroke="#0f172a" strokeDasharray="4 3"
                  label={{ value: t("retention.hookEnd"), position: "insideTopRight", fontSize: 10, fill: "#0f172a" }}
                />
              )}
              <Area type="monotone" dataKey="retencion" stroke="#dc2626" strokeWidth={2} fill="url(#gradRet)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Con 100 puntos repartidos por todo el vídeo, cuanto más largo es, menos resolución hay
          justo en el hook. Decirlo evita que se lean los 5s como una medición exacta. */}
      {data.resolucion_sec > 5 && (
        <p className="text-xs text-slate-500">{t("retention.resolution", { s: data.resolucion_sec })}</p>
      )}
    </div>
  );
}

function Hito({ label, value, destacado = false }) {
  return (
    <div>
      <div className={destacado ? "text-2xl font-bold font-head text-red-700" : "text-lg font-bold font-head text-slate-700"}>
        {value}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}
